import * as _ from 'lodash'
import {IBusinessObject} from '../PA.Core/PA.Core.IF.BO';
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {
    WB_ModelPredictorRelationshipRow,
    WB_ModelPredictorRow, WB_ModelRow, WB_ModelTermRelationshipRow,
    WB_ModelTermRow
} from "../PA.Core/PAdbSchemaRows";
import {IwbModelTerm} from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_Model,
    esWB_Model_Predictor,
    esWB_Model_Predictor_Relationship,
    esWB_Model_Tag_Relationship,
    esWB_Model_Term,
    esWB_Model_Term_Relationship,
    esWB_Modeling_Session
} from "../PA.Core/PAdbSchema";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {ASync} from "../PA.Core/Basic/ASync";
import {ModelTermBOValidations} from "./ModelTermBO.Validations";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import { LeaseRegistry } from '../PA.Utils/LeaseRegistry';

export class ModelTermBO implements IBusinessObject
{
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_ModelTerm: WB_ModelTermRow;

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New Model Term'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_ModelTerm = null;
    }

    get WB_ModelTerm(): WB_ModelTermRow {
        return this.drWB_ModelTerm;
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Model Term");
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, error)=>{

            self.LoadOptions = options || {};
            if('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if(this.dbContext == null) {
                let sError = "Unable to determine dbContext for new Model Term.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_Model_Term)
             .then((newModelTermRow: WB_ModelTermRow)=>{
                    newModelTermRow.setDefaults();
                    self.drWB_ModelTerm = newModelTermRow;
                    self.dbContext.add(esWB_Model_Term, newModelTermRow);
                    self.Identity.setAll(self.WB_ModelTerm.cif.WB_MODEL_TERM_ID, self.WB_ModelTerm.cif.TERM_ID, self.WB_ModelTerm.cif.TERM_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_ModelTerm, 'WB_MODEL_TERM_ID','TERM_ID','TERM_NAME');
                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);
                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err);
                    error(err)
                })
        });

        return this._OnLoadComplete;
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error)=>{

            if (self.bIsLoaded) {
                let sError = "Model Term already loaded.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            self.LoadOptions = options || {};

            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if(self.dbContext == null)
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                let sError = "Unable to determine dbContext for Model Term.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            if('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any> self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = ModelTermBO.createRecordResolver({dbContext: self.dbContext});
                recordResolverLoadPromise = ModelTermBO.setupRecordResolver(recordResolver, [anId])
            }

            recordResolverLoadPromise
                .then(function () {
                    let newModelTermRow: any = recordResolver.get(esWB_Model_Term, anId);

                    if(_.isEmpty(newModelTermRow))
                    {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception("Id not found.");
                        resolve(self);
                        return;
                    }

                    self.drWB_ModelTerm = newModelTermRow;
                    self.Identity.setAll(self.WB_ModelTerm.cif.WB_MODEL_TERM_ID, self.WB_ModelTerm.cif.TERM_ID, self.WB_ModelTerm.cif.TERM_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_ModelTerm, 'WB_MODEL_TERM_ID','TERM_ID','TERM_NAME');
                })
                .then((newModelTermRow:any) => {
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch((err:any)=>{
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });

        });

        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    Validate(options?: any): Promise<IBusinessObject> {

        let self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start("Validating " + self.WB_ModelTerm.cif.TERM_NAME + "(" + self.WB_ModelTerm.cif.TERM_ID + ")");
        return new Promise<IBusinessObject>((resolve, reject) => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            if (self.dbContext == undefined) {
                let sError = "Unable to determine dbContext to Validate Model Term.";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
            }

            ModelTermBOValidations.ValidateEmptyFields(self)
                .then((ignore) => {
                    //return  next validation
                    // ()then hanadler

                    if(self.ValidateMessageProvider.countMessages(MessageType.Error) > 0){
                        self._bIsValidated = false;
                    } else {
                        self._bIsValidated = true;
                    }
                    self.ValidateProgressProvider.finish();
                    resolve(self);

                })
                .catch((err) => {
                    self._bIsValidated = false;
                    self.ValidateProgressProvider.exception(err);
                    resolve(self);

                })
        });

    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    private SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {

        const self = this;
        const wbModelTerm = <IwbModelTerm>self.drWB_ModelTerm;
        this.SaveProgressProvider.start("Saving " + wbModelTerm.TERM_NAME + "(" + wbModelTerm.TERM_ID + ")");

        return new Promise<IBusinessObject>((resolve, reject) => {
            // Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // We could search the tree if there is any record where store isn't set.

            if (self.dbContext == null) {
                self._bIsSaved = false;
                let sError = "Unable to determine dbContext to Save Model Term.";
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return
            }

            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            self.Validate({})
                .then((ignore) => {
                    if (!self._bIsValidated) {
                        self._bIsSaved = false;
                        self.SaveProgressProvider.finish();
                        resolve(self);
                        return
                    }
                    return saveFunction(esWB_Model_Term, self.drWB_ModelTerm, {})
                        .then((ignored) => {
                            return self.maintainModelComponents();
                        })
                        .then((arrTasks: WB_ModelPredictorRelationshipRow[]) => {

                            let tasks: Promise<any>[] = [];

                            tasks.push(saveFunction(esWB_Model_Term, self.drWB_ModelTerm));
                            tasks.push(saveFunction(esWB_Model_Predictor_Relationship, arrTasks));
                            return Promise.all(tasks);
                        })
                        .then((ignored) => {
                            if (bBatch) {
                                self.dbContext.addBatchCompleteListener(() => {
                                    self._bIsSaved = true;
                                    LeaseRegistry.generateLeaseInvalidationByClass(LeaseRegistry.getLeaseClass(LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_Model)));
                                })
                            } else {
                                self._bIsSaved = true;
                                LeaseRegistry.generateLeaseInvalidationByClass(LeaseRegistry.getLeaseClass(LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_Model)));
                            }
                            self.SaveProgressProvider.finish();
                            resolve(self);
                        })
                        .catch((err) => {
                            self.SaveProgressProvider.exception(err);
                            reject(self);
                        })
                })
                .catch((err) => {
                    self.SaveProgressProvider.exception(err);
                    reject(self);
                })

        })
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    //Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbModelTerm  = <IwbModelTerm>self.drWB_ModelTerm;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbModelTerm.TERM_NAME + "(" + wbModelTerm.TERM_ID + ")");

        return new Promise<IBusinessObject>((resolve, error) => {

            // 1. Validate if we can delete the Customer

            // 2. If Valid, mark all the records for deletion.
            self.drWB_ModelTerm.markDeleted();

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);
                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err);
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    async maintainModelComponents(): Promise<WB_ModelPredictorRelationshipRow[]> {
        const self = this;

        let models: WB_ModelTermRelationshipRow[] = await self.dbContext.findAll(esWB_Model_Term_Relationship, {
            where: {
                WB_MODEL_TERM_ID: self.drWB_ModelTerm.cif.WB_MODEL_TERM_ID,
                DELETED_IND: {neq: '1'}
            }
        })
        let modelIds: number[] = _.map(models, aRow => aRow.cif.WB_MODEL_ID)

        let aRecordResolver = new ESRecordResolver(self.dbContext,
            {
                dctExcludeHasManyNamedRelations: {
                    [esWB_Model]: [esWB_Model_Tag_Relationship, esWB_Modeling_Session]
                }
            })

        await self.dbContext.purgeCache(esWB_Model, modelIds)
        let arrModelRow: WB_ModelRow[] = <WB_ModelRow[]>await aRecordResolver.queryAllWithRelationships(esWB_Model, {
            WB_MODEL_ID: {
                in: modelIds
            },
            DELETED_IND: {neq: '1'}
        })

        let arrTasks: WB_ModelPredictorRelationshipRow[] = []

        // Predictors
        // Find & Delete Duplicates and Extras
        for(let aModelRow of _.values(arrModelRow)) {
            let dctPredictorId: { [wbPredictorId: number]: number } = {}
            let dctCoeffId: { [wbCoeffId: number]: number } = {}
            let dctConstantId: { [wbConstantId: number]: number } = {}

            for (let aTermRelRow of aModelRow.WB_ModelTermRelationshipRows) {
                if (!aTermRelRow.isActive) {
                    continue
                }

                if (!aTermRelRow.WB_ModelTermRow) {
                    continue
                }

                let aTerm = aTermRelRow.WB_ModelTermRow
                let aFunction = aTerm.WB_ModelFunctionRow

                _.forEach(['WB_MODEL_PREDICTOR_ID', 'WB_MODEL_PREDICTOR_ID2', 'WB_MODEL_PREDICTOR_ID3', 'WB_MODEL_PREDICTOR_ID4'],
                    (aFieldName, idx) => {
                        let wbPredictorId = _.toNumber((<any>aTerm)[aFieldName])
                        if (idx >= aFunction.cif.PREDICTOR_COUNT)
                            return

                        if (wbPredictorId > 0) {
                            dctPredictorId[wbPredictorId] = wbPredictorId
                        }

                    })

                _.forEach(['WB_MODEL_COEFFICIENT_ID', 'WB_MODEL_COEFFICIENT_ID2', 'WB_MODEL_COEFFICIENT_ID3', 'WB_MODEL_COEFFICIENT_ID4'],
                    (aFieldName, idx) => {
                        let wbCoeffId = _.toNumber((<any>aTerm)[aFieldName])
                        if (idx >= aFunction.cif.COEFFICIENT_COUNT)
                            return

                        if (wbCoeffId > 0) {
                            dctCoeffId[wbCoeffId] = wbCoeffId
                        }

                    })

                _.forEach(['WB_MODEL_CONSTANT_ID', 'WB_MODEL_CONSTANT_ID2', 'WB_MODEL_CONSTANT_ID3', 'WB_MODEL_CONSTANT_ID4'],
                    (aFieldName, idx) => {
                        let wbConstantId = _.toNumber((<any>aTerm)[aFieldName])
                        if (idx >= aFunction.cif.CONSTANT_COUNT)
                            return

                        if (wbConstantId > 0) {
                            dctConstantId[wbConstantId] = wbConstantId
                        }

                    })
            }

            let arrWBPredictorRow: WB_ModelPredictorRow[]
                = <any>await self.dbContext.findUsingCache(esWB_Model_Predictor, _.values(dctPredictorId))

            let dctPredictors = _.keyBy(arrWBPredictorRow, (a) => a.cif.WB_MODEL_PREDICTOR_ID)

            let dctPredictorIdExists: { [wbPredictorId: number]: number } = {}

            let dctPredictorsMissing = _.clone(dctPredictors)

            _.forEach(aModelRow.WB_ModelPredictorRelationshipRows, aRow => {
                if (!aRow.isActive) {
                    return
                }
                let wbPredictorId = aRow.cif.WB_MODEL_PREDICTOR_ID
                if (wbPredictorId in dctPredictorIdExists) {
                    // Remove dups
                    aRow.markDeleted()
                    return
                }
                if (wbPredictorId in dctPredictors) {
                    delete dctPredictorsMissing[wbPredictorId]
                    dctPredictorIdExists[wbPredictorId] = wbPredictorId
                } else {
                    aRow.markDeleted()
                }

                arrTasks.push(aRow)
            })

            // Add Missing
            for (let aPredictorRow of _.values(dctPredictorsMissing)) {
                let newRow: WB_ModelPredictorRelationshipRow = <any>await self.dbContext.createRecordWithKey(esWB_Model_Predictor_Relationship)
                newRow.setDefaults()
                newRow.cif.WB_MODEL_ID = aModelRow.cif.WB_MODEL_ID
                newRow.WB_ModelRow = aModelRow
                newRow.cif.WB_MODEL_PREDICTOR_ID = aPredictorRow.cif.WB_MODEL_PREDICTOR_ID
                newRow.WB_ModelPredictorRow = aPredictorRow
                self.dbContext.add(esWB_Model_Predictor_Relationship, newRow)

                arrTasks.push(newRow)
            }
        }
        return arrTasks

    }


    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }

        const modelTerm_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {}
        };

        const aRecResolver = new ESRecordResolver(dbContext, modelTerm_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_Model_Term,ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

    //</editor-fold>
}


PAEnvironment.registerBusinessObjectClass( ModelTermBO ,"ModelTermBO", "Model Term")
